/**
 * The step sequence for Journeys A and B, and the single source of the screen
 * order.
 *
 * ### A disagreement that is recorded rather than resolved
 *
 * `component-spec.md` §10 says the stepper has **six** steps for SIM-only and
 * nine with a device. The prototype's `stepDefs()` builds **seven** and nine, and
 * aliases S-12 onto S-11 so the device-detail screen shares a step with the
 * device list.
 *
 * Step labels are appearance, and CLAUDE.md is explicit that the prototype wins
 * on appearance — so seven is implemented. But the discrepancy is not a typo to
 * silently correct: if the answer really is six, then one of these screens is not
 * a step in the journey, and which one is a question for the journeys document
 * rather than for this file. It is recorded in `docs/SCREEN-MAP.md`.
 *
 * ### Why the order lives here and not in the router
 *
 * Both the stepper and the back/continue pair at the foot of every screen need
 * "what comes before and after this, given the variant". Deriving it from the
 * route table would mean the router knew about the device variant, which it does
 * not and should not — `/migrare/telefoane` is a legal route whose presence in
 * the journey depends on a choice made on S-03.
 */
import type { Step } from '../../ui/stepper/stepper.component';

/**
 * The screens that make up Journeys A and B — the ones with a position in the
 * sequence and therefore a step, a previous and a next.
 */
export type FlowScreenId =
  | 'S-01'
  | 'S-03'
  | 'S-04'
  | 'S-05'
  | 'S-06'
  | 'S-07'
  | 'S-08'
  | 'S-09'
  | 'S-10'
  | 'S-11'
  | 'S-12'
  | 'S-13';

/**
 * Every screen identifier, stable across the journeys document, the routes, the
 * components and any tickets (CLAUDE.md §Conventions).
 *
 * The six beyond the flow are not steps and have no place in the sequence: S-02 is
 * the SSO stand-in, S-14 … S-17 are the post-order statuses of one route, S-18 and
 * S-19 are My Orders, and S-20 is a pattern rather than a screen. They still need
 * identifiers, because a route carries one and the annotation layer keys on it.
 */
export type ScreenId =
  | FlowScreenId
  | 'S-02'
  | 'S-14'
  | 'S-15'
  | 'S-16'
  | 'S-17'
  | 'S-18'
  | 'S-19'
  | 'S-20';

export type FlowVariant = 'sim-only' | 'with-device';

interface StepDef {
  readonly screen: FlowScreenId;
  readonly label: string;
  /** Screens that share this step rather than having one of their own. */
  readonly alsoCoveredBy?: readonly FlowScreenId[];
}

/**
 * Seven steps. S-01 is not a step — it is the entry point, before the journey
 * starts — and S-10 is not either, because the order is already placed.
 */
const SIM_ONLY: readonly StepDef[] = [
  { screen: 'S-03', label: $localize`:@@step.offer:Ofertă` },
  { screen: 'S-04', label: $localize`:@@step.number:Număr` },
  { screen: 'S-05', label: $localize`:@@step.verify:Verificare` },
  { screen: 'S-06', label: $localize`:@@step.sim:SIM` },
  { screen: 'S-07', label: $localize`:@@step.cart:Coș` },
  { screen: 'S-08', label: $localize`:@@step.payment:Plată` },
  { screen: 'S-09', label: $localize`:@@step.confirm:Confirmare` },
];

/**
 * Nine steps. S-12 (device detail, where the instalment check runs) shares the
 * "Telefon" step with S-11 rather than adding a tenth — the prototype's aliasing.
 */
const WITH_DEVICE: readonly StepDef[] = [
  { screen: 'S-03', label: $localize`:@@step.offer:Ofertă` },
  { screen: 'S-11', label: $localize`:@@step.device:Telefon`, alsoCoveredBy: ['S-12'] },
  { screen: 'S-13', label: $localize`:@@step.instalments:Rate` },
  { screen: 'S-04', label: $localize`:@@step.number:Număr` },
  { screen: 'S-05', label: $localize`:@@step.verify:Verificare` },
  { screen: 'S-06', label: $localize`:@@step.sim:SIM` },
  { screen: 'S-07', label: $localize`:@@step.cart:Coș` },
  { screen: 'S-08', label: $localize`:@@step.payment:Plată` },
  { screen: 'S-09', label: $localize`:@@step.confirm:Confirmare` },
];

const defsFor = (variant: FlowVariant): readonly StepDef[] =>
  variant === 'with-device' ? WITH_DEVICE : SIM_ONLY;

/** Which step a screen belongs to, following the S-12 → S-11 aliasing. */
const indexOfScreen = (defs: readonly StepDef[], screen: ScreenId): number =>
  defs.findIndex(
    (d) =>
      d.screen === screen ||
      // `includes` narrows its argument to the array's element type, and this is
      // reached with ids that are legitimately not in the flow at all — they
      // simply match nothing.
      (d.alsoCoveredBy as readonly ScreenId[] | undefined)?.includes(screen) === true,
  );

/**
 * The stepper's input for a given screen. Empty when the screen is not in the flow.
 *
 * `skipped` names steps the customer went past without doing, which position alone
 * cannot tell: one path in Journey B — a declined instalment decision treated as
 * `OFFER_FULL_PRICE` — goes from S-12 to S-04 and never visits S-13. Without this the
 * bar would report "Rate ✓ finalizat" for a screen that was never rendered. It is a
 * caller's fact, not a derivable one, so it is passed in; see `ShellComponent.steps`.
 */
export function stepsFor(
  variant: FlowVariant,
  screen: ScreenId,
  skipped: readonly FlowScreenId[] = [],
): readonly Step[] {
  const defs = defsFor(variant);
  const current = indexOfScreen(defs, screen);
  if (current < 0) return [];

  return defs.map((def, i) => ({
    id: def.screen,
    label: def.label,
    state:
      i === current
        ? 'current'
        : i > current
          ? 'upcoming'
          : // A step behind the current one is finalised unless the caller says the
            // customer was routed around it.
            skipped.includes(def.screen)
            ? 'skipped'
            : 'done',
  }));
}

/**
 * The full screen order including the two that are not steps.
 *
 * S-01 opens the journey; S-10 closes it. Neither appears in the stepper, but
 * both need a "what comes next" answer for the continue button.
 */
export function screenOrder(variant: FlowVariant): readonly FlowScreenId[] {
  return ['S-01', ...defsFor(variant).map((d) => d.screen), 'S-10'];
}

export function nextScreen(variant: FlowVariant, screen: ScreenId): FlowScreenId | null {
  const order = screenOrder(variant);
  const i = order.indexOf(screen as FlowScreenId);
  return i >= 0 && i < order.length - 1 ? (order[i + 1] ?? null) : null;
}

export function previousScreen(variant: FlowVariant, screen: ScreenId): FlowScreenId | null {
  const order = screenOrder(variant);
  // S-12 is not in the order — it hangs off S-11 — so going back from it means
  // going back to the list it was opened from.
  if (screen === 'S-12') return 'S-11';
  const i = order.indexOf(screen as FlowScreenId);
  return i > 0 ? (order[i - 1] ?? null) : null;
}

/**
 * Flow screen id → route path. Romanian paths mirroring the real eShop.
 *
 * Only the flow screens are here. The others are routed too, but their paths are
 * not derived from a sequence — `/comenzile-mele` is a destination, not a step —
 * so putting them in this map would imply an order that does not exist.
 */
export const ROUTE_FOR: Readonly<Record<FlowScreenId, string>> = {
  'S-01': '/migrare',
  'S-03': '/migrare/oferte',
  'S-04': '/migrare/numar',
  'S-05': '/migrare/verificare',
  'S-06': '/migrare/sim',
  'S-07': '/migrare/cos',
  'S-08': '/migrare/plata',
  'S-09': '/migrare/confirmare',
  // The post-order screen is per-order, so its path needs a reference appended.
  'S-10': '/migrare/comanda',
  'S-11': '/migrare/telefoane',
  'S-12': '/migrare/telefon',
  'S-13': '/migrare/rate',
};
